import { useEffect, useState, useRef } from 'react';
import AuthProvider from '../services/auth/AuthProvider/Index';
import { ClinicInfo, getClinicInfo } from '../../api/provider';

type ErrorSetter = (message: string | null) => void;

interface BootstrapOptions {
  onReady: () => Promise<void>;
  onIdentity?: (identity: any) => void;
  setError: ErrorSetter;
}

/**
 * Shared bootstrap hook for authenticated provider views.
 * Handles token validation, identity loading, clinic fetch, and session lifespan checks.
 */
export const useProviderBootstrap = ({
  onReady,
  onIdentity,
  setError,
}: BootstrapOptions) => {
  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [provider, setProvider] = useState<any>(null);
  
  // Use refs to store callbacks to avoid infinite loops
  const onReadyRef = useRef(onReady);
  const onIdentityRef = useRef(onIdentity);
  const setErrorRef = useRef(setError);
  
  // Update refs when callbacks change
  useEffect(() => {
    onReadyRef.current = onReady;
    onIdentityRef.current = onIdentity;
    setErrorRef.current = setError;
  }, [onReady, onIdentity, setError]);

  useEffect(() => {
    const fetchClinic = async () => {
      try {
        const data = await getClinicInfo();
        setClinic(data);
      } catch (error) {
        console.error('Error fetching clinic info:', error);
        setErrorRef.current('Failed to load clinic information');
      }
    };

    const initializeAuth = async () => {
      try {
        const token = await AuthProvider.getValidAuthorizationToken();
        if (!token) {
          setErrorRef.current('Authentication failed. You will be redirected to login.');
          return;
        }

        const identity = await AuthProvider.getIdentity();
        if (identity) {
          setProvider(identity);
          onIdentityRef.current?.(identity);
          await Promise.all([onReadyRef.current(), fetchClinic()]);
        } else {
          setErrorRef.current('Failed to load user information.');
        }
      } catch (error) {
        console.error('Authentication error:', error);
        setErrorRef.current('An error occurred during authentication.');
      }
    };

    const checkSessionLifespan = async () => {
      const sessionLifespan = await AuthProvider.getMinimumSessionLifespan();
      if (sessionLifespan < 300) {
        console.warn('Session expiring soon.');
      }
    };

    initializeAuth();
    checkSessionLifespan();
    const intervalId = setInterval(checkSessionLifespan, 60000);
    return () => clearInterval(intervalId);
  }, []); // Empty dependency array - only run once on mount

  return { clinic, provider };
};

